import React from "react";

import {
  AccountCircleRounded,
  AndroidRounded,
  CheckCircleRounded,
  CloseRounded,
  FormatColorFillRounded,
  KeyboardArrowDownRounded,
  RefreshRounded,
  SecurityUpdateRounded,
  WebAssetRounded,
} from "@mui/icons-material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import MarkChatUnreadRoundedIcon from "@mui/icons-material/MarkChatUnreadRounded";
import { Box, ButtonBase, Chip, CircularProgress, Skeleton } from "@mui/material";
import { deleteField } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import useAuth from "../../../config/context/authContext";
import useFirebase from "../../../config/context/firebaseContext";
import useModal from "../../../config/context/modalContext";
import { AuthService } from "../../../config/services/AuthService";
import { SpottService } from "../../../config/services/SpottService";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";
import { UtilComponents } from "../../../config/util/utilComponents";

import ChipMain from "../../../components/InputMain/ChipMain";
import SwitchMain from "../../../components/InputMain/SwitchMain";
import MainBorderButton from "../../../components/MainBorderButton";
import MainText from "../../../components/MainText";
import ModalNotify from "../../../components/ModalNotify";
import PageHeader from "../../../components/PageHeader";

import { SpottPostType, UserInfoType, UserTypesAvailable } from "../../../types/database";

export function UserModalContentDetails({ user }: { user: UserInfoType }) {
  return (
    <Box>
      <Box sx={{ whiteSpace: "break-spaces", wordWrap: "break-word" }}>
        <MainText size="caption">ID:</MainText>
        <Box mb={1}>
          <MainText size="caption" color="regular">
            {user?.id}
          </MainText>
        </Box>
        <MainText size="caption">Token:</MainText>
        <Box mb={1}>
          <MainText size="caption" color="regular">
            {user?.fcmToken}
          </MainText>
        </Box>
        <MainText size="caption">Device:</MainText>

        <Box display={"flex"} justifyContent={"start"} alignItems={"center"}>
          <MainText size="caption" color="regular">
            Mobile:{" "}
          </MainText>
          {user?.device?.mobile ? (
            <CheckCircleRounded fontSize="inherit" />
          ) : (
            <CloseRounded fontSize="inherit" color="secondary" />
          )}
        </Box>
        <Box display={"flex"} justifyContent={"start"} alignItems={"center"}>
          <MainText size="caption" color="regular">
            Standalone:{" "}
          </MainText>
          {user?.device?.standalone ? (
            <CheckCircleRounded fontSize="inherit" />
          ) : (
            <CloseRounded fontSize="inherit" color="secondary" />
          )}
        </Box>

        <MainText size="caption" color="regular">
          Plataforma: {user?.device?.platform}
        </MainText>
        <MainText size="caption" color="regular">
          OS: {user?.device?.os}
        </MainText>
        <MainText size="caption" color="regular">
          Vendor: {user?.device?.vendor}
        </MainText>
      </Box>
    </Box>
  );
}
export default function UserPage() {
  const navigate = useNavigate();

  const params = useParams();

  const { currentUserState } = useAuth();
  const { pushAlert, alertConfirm } = useModal();
  const { functions } = useFirebase();

  const AuthOne = AuthService.One();
  const AuthUpdate = AuthService.Update();
  const SpottList = SpottService.ListFromUser();

  const [showModalNotify, setShowModalNotify] = React.useState(false);

  const [statusLoading, setStatusLoading] = React.useState(false);

  const [userTypeField, setUserTypeField] = React.useState<UserTypesAvailable | null>();
  const [userEnabledField, setUserEnabledField] = React.useState(false);

  const [deviceType, setDeviceType] = React.useState<"app" | "web" | "pwa">();

  const reloadUserInfo = () => {
    if (params.userId)
      AuthOne.runRequest(params.userId).catch((err) =>
        pushAlert({
          type: "error",
          message: "Erro ao obter usuário. " + (err as Error).message,
        })
      );
  };

  const setAdmin = async () => {
    const setMeAsAdmin = httpsCallable(functions, "setMeAsAdmin");
    const result = await setMeAsAdmin({});
    console.log(...new Dev("setAdmin", result).Info());
  };

  const handleChangeClaims = async (enabled: boolean, type: UserTypesAvailable) => {
    try {
      pushAlert({
        isConfirm: true,
        type: "warning",
        onConfirm: async () => {
          try {
            setStatusLoading(true);
            const changeUserClaims = httpsCallable(functions, "changeUserClaims");

            const payload = {
              userId: AuthOne.item?.id,
              enabled,
              type,
            };

            console.log(...new Dev("handleChangeClaims Payload", payload).Info());

            const result = await changeUserClaims(payload);

            pushAlert({
              type: "success",
              message: "Usuário alterado",
            });
          } catch (error) {
            console.log(...new Dev("handleChangeClaims onConfirm", error).Error());
            pushAlert({
              type: "error",
              message: "Erro ao alterar usuário. " + (error as Error).message,
            });
          } finally {
            setStatusLoading(false);
          }
        },
        onDimiss: () => handleFetchUser(),
        message: "Você deseja realmente alterar o tipo deste usuário?",
      });
    } catch (error: any) {
      console.log(...new Dev("handleChangeClaims", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao alterar usuário",
      });
    }
  };

  const handleFetchUser = () => {
    if (params.userId)
      AuthOne.runRequest(params.userId).catch((err) =>
        pushAlert({
          type: "error",
          message: "Erro ao obter usuário. " + (err as Error).message,
        })
      );
    else navigate(-1);
  };

  const handleShowDetails = () => {
    pushAlert({
      title: "Detalhes",
      isClosable: true,
      children: AuthOne?.item ? <UserModalContentDetails user={AuthOne.item} /> : <Box />,
    });
  };

  const handleRemoveColor = async () => {
    await AuthUpdate.runRequest(AuthOne.item?.id || "", { color: deleteField() })
      .then((res) => {
        pushAlert({
          type: "success",
          message: "Usuario atualizado.",
        });
      })
      .catch((err) => {
        pushAlert({
          type: "error",
          message: "Erro ao alterar usuário. " + (err as Error).message,
        });
      });

    reloadUserInfo();
  };

  const handleShowColorModal = () => {
    if (AuthOne.item?.color)
      pushAlert({
        isConfirm: true,
        children: (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2,
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
              <MainText>A cor atual é {AuthOne.item?.color}</MainText>

              <Box sx={{ color: AuthOne.item?.color, ml: 1 }}>
                <FormatColorFillRounded color="inherit" />
              </Box>
            </Box>

            <MainText>Deseja remover?</MainText>
          </Box>
        ),
        onConfirm: () => handleRemoveColor(),
      });
  };

  React.useEffect(() => {
    handleFetchUser();
  }, []);

  React.useEffect(() => {
    if (AuthOne?.item) {
      console.log(...new Dev("User:", AuthOne.item).Info());
      setUserTypeField(AuthOne?.item.type);
      setUserEnabledField(AuthOne?.item.enabled ? true : false);
      setDeviceType(
        AuthOne.item?.device?.mobile === true
          ? "app"
          : AuthOne.item?.device?.standalone === true
          ? "pwa"
          : "web"
      );
    }
  }, [AuthOne.item]);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          leftIcon={<ArrowBackIosNewRoundedIcon />}
          onLeftAction={() => navigate(-1)}
          title="Usuário"
          rightIcon={<RefreshRounded />}
          onRightAction={() => reloadUserInfo()}
        />

        {/* <MainSolidButton text="setAdmin" onClick={setAdmin} /> */}

        {showModalNotify && AuthOne?.item?.fcmToken ? (
          <ModalNotify fcmToken={AuthOne.item.fcmToken} onClose={() => setShowModalNotify(false)} />
        ) : null}

        {AuthOne.isLoading ? (
          <>
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={40}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={150}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
          </>
        ) : (
          <>
            <Box justifyContent={"start"} display={"flex"}>
              {AuthUpdate.isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="circular"
                  width={36}
                  height={36}
                  sx={{ mr: 1 }}
                />
              ) : (
                <ButtonBase sx={{ mr: 1, borderRadius: "50%" }} onClick={handleShowColorModal}>
                  <AccountCircleRounded
                    sx={{
                      fontSize: "36px",
                      color: AuthOne.item?.color || GreyPallet.Black,
                    }}
                  />
                </ButtonBase>
              )}

              <MainText color="black" size="subtitle">
                {AuthOne.item?.name || "Usuário sem nome"}
              </MainText>
            </Box>
            <Box my={2}>
              <ButtonBase
                sx={{ maxWidth: "100%", borderRadius: "10px" }}
                onClick={() =>
                  UtilComponents.copyToClipboard(
                    pushAlert,
                    AuthOne.item?.email || "-",
                    "Email copiado para área de transferência!"
                  )
                }
              >
                <MainText color="black" size="subtitle" canOverflow>
                  {AuthOne.item?.email || "-"}
                </MainText>
              </ButtonBase>
            </Box>
            <Box
              display={"flex"}
              justifyContent="start"
              alignItems={"center"}
              color={GreyPallet.Regular}
            >
              <Chip
                label={
                  <Box display={"flex"} alignItems={"center"} style={{ color: GreyPallet.Regular }}>
                    {deviceType === "app" ? (
                      <AndroidRounded fontSize={"small"} sx={{ mr: 1 }} />
                    ) : deviceType === "pwa" ? (
                      <SecurityUpdateRounded fontSize={"small"} sx={{ mr: 1 }} />
                    ) : (
                      <WebAssetRounded fontSize={"small"} sx={{ mr: 1 }} />
                    )}
                    <MainText size="small" color="regular">
                      {AuthOne.item?.device?.os || "-"} - {AuthOne.item?.device?.platform || "-"}
                    </MainText>
                  </Box>
                }
                variant={"filled"}
                style={{ borderWidth: 3 }}
                onClick={handleShowDetails}
              />
            </Box>

            <Box my={2}>
              <MainText color="regular" size="body">
                {AuthOne.item?.createdAtTimestamp
                  ? DateTime.fromMillis(AuthOne.item?.createdAtTimestamp).toFormat("dd/LL/y HH:mm")
                  : "-"}
              </MainText>
            </Box>

            {/* Enabled */}

            {currentUserState?.type === "admin" ? (
              <Box style={{ border: `3px solid ${GreyPallet.Light}`, borderRadius: 15 }} p={2}>
                <Box display={"flex"} mb={2} justifyContent={"space-between"}>
                  <Box gap={1} display={"flex"}>
                    <ChipMain
                      disabled={statusLoading}
                      options={[
                        { name: "Admin", value: "admin" },
                        { name: "Staff", value: "staff" },
                        { name: "User", value: "user" },
                      ]}
                      onChange={(value) => {
                        handleChangeClaims(userEnabledField, value as UserTypesAvailable);
                        setUserTypeField(value as UserTypesAvailable);
                      }}
                      value={String(userTypeField)}
                    />
                  </Box>

                  {statusLoading && <CircularProgress size={20} color="inherit" />}
                </Box>

                <SwitchMain
                  name="Usuário habilitado"
                  disabled={statusLoading}
                  checked={userEnabledField}
                  noBottom
                  onChange={(checked) => {
                    setUserEnabledField(checked);
                    handleChangeClaims(checked, userTypeField || "user");
                  }}
                />
              </Box>
            ) : null}

            {/* Notify */}

            {AuthOne.item?.fcmToken ? (
              <Box mt={4}>
                <MainBorderButton
                  color="black"
                  text="Notificar"
                  icon={<MarkChatUnreadRoundedIcon />}
                  onClick={() => setShowModalNotify(true)}
                />
              </Box>
            ) : null}
          </>
        )}

        {/* Posts */}

        <Box mt={4} mb={2}>
          <MainText color="black" size="title">
            Postagens
          </MainText>
        </Box>

        {SpottList.items?.length ? (
          SpottList.items.map((item: SpottPostType) => (
            <ButtonBase
              key={item.id}
              sx={{ mb: 1 }}
              onClick={() => navigate("/post/" + item.id)}
              style={{
                border: `3px solid ${GreyPallet.Light}`,
                borderRadius: 15,
                width: "100%",
                overflow: "hidden",
                marginBottom: 20,
              }}
            >
              <Box
                style={{
                  textAlign: "start",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                px={2}
                py={1}
              >
                <Box
                  mb={1}
                  style={{
                    textAlign: "start",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <MainText size="small" color="regular">
                    {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                  </MainText>
                  {item.files.length > 0 && (
                    <ImageRoundedIcon style={{ fontSize: 15, color: GreyPallet.Regular }} />
                  )}
                </Box>

                <MainText size="caption" color="black">
                  {item.text}
                </MainText>
              </Box>
            </ButtonBase>
          ))
        ) : (
          <Box>
            <MainBorderButton
              color="black"
              loading={SpottList.isLoading}
              text="Carregar Postagens"
              icon={<KeyboardArrowDownRounded />}
              onClick={() => {
                if (AuthOne.item)
                  SpottList.runRequest(String(AuthOne.item.id)).catch((err) =>
                    pushAlert({
                      type: "error",
                      message: "Erro ao obter postagens. " + (err as Error).message,
                    })
                  );
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
